import { Container, InteractionEvent, PointerEvents } from '@tencent/alloy-renderer';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import {
    GuideLineContainer, IGuideLineContainerProps,
} from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/component/guide-component/components/guide-line-container';
import { IReactionDisposer, reaction, transaction } from 'mobx';
import {
    EPpGuideOrientation,
    IGuide,
} from '@src/slide/domain/ui-model/element-modeles/Interface/guide-model.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import { INSERT_SLIDE_ORIGIN_TYPE } from '@src/js/constant';
import { IGuidesManager } from '@src/slide/application/service/guides-manager/index.interface';
import { convertPxToCm } from '@src/slide/infrastructure/module/independent/unit';
import { EArrowDirection } from '@com/guides-line/arrow-tips';
import { IGridLineManager } from '@src/slide/application/service/grid-line-manager/index.interface';
import {
    IGuideLineComponent,
} from '@src/slide/content-viewer/service/canvas-renderer/entry/base/feature/component/guide-component/interface/guide-line.interface';
import { EDIT_TYPE } from '@src/slide/domain/selection/constant/edit-type';
import ArrowTips from '@com/guides-line/arrow-tips-api';

interface IGuideComponentProps {
    type: ESourceType;
    isUiComponent: boolean;
}

export enum ESourceType {
    slide,
    master,
    layout,
}

// 吸附范围
const STICKY_WIDTH = 10;

interface IMenuProps {
    top: number;
    left: number;
    minWidth: number;
    subMinWidth: number;
    onSubMenuVisibleChange(menuData);
}

/**
 * 参考线容器
 */
export class GuideComponent extends Container {
    private vertContainer: GuideLineContainer;
    private horzContainer: GuideLineContainer;
    // private scale: number;
    private type: ESourceType;
    private vertGuides: Array<IGuide>;
    private horzGuides: Array<IGuide>;
    private cachedVertGuides: Array<number>;
    private cachedHorzGuides: Array<number>;
    private reaction: IReactionDisposer;
    private reaction2: IReactionDisposer;
    private menuManager?: IMenuManager;
    private guidesManager?: IGuidesManager;
    private gridLineManager?: IGridLineManager;
    private currentComponent?: IGuideLineComponent;
    private slideWidth: number;
    private slideHeight: number;
    private isUiComponent: boolean;
    private cacheMousePosition: {
        x: number;
        y: number;
        originPos: number;
    } = { x: 0, y: 0, originPos: 0 };

    public constructor(props: IGuideComponentProps) {
        super();
        const { type, isUiComponent } = props;
        this.type = type;
        this.isUiComponent = isUiComponent;
        this.menuManager = this.getAppService().editableServiceLayer?.menuManager;
        this.guidesManager = this.getAppService().editableServiceLayer?.guidesManager;
        this.gridLineManager = this.getAppService().editableServiceLayer?.gridLineManager;
        this.slideWidth = this.getAppService().getUiDataService().presentation.props.slideWidth;
        this.slideHeight = this.getAppService().getUiDataService().presentation.props.slideHeight;
        if (this.isUiComponent) {
            this.pointerEvents = PointerEvents.NONE;
        } else {
            this.pointerEvents = PointerEvents.AUTO;
        }
        this.initReaction();
        this.renderGuides();
    }

    public updateSlide = (slideWidth, slideHeight) => {
        this.slideWidth = slideWidth;
        this.slideHeight = slideHeight;
    };

    public renderGuides = () => {
        this.getGuidesData();
        this.renderHorztContainer();
        this.renderVertContainer();
    };

    public dispose = () => {
        super.dispose();
        this.reaction?.();
        this.reaction2?.();
        this.vertContainer?.dispose();
        this.horzContainer?.dispose();
    };

    public renderVertContainer = () => {
        const { slideLeft, scale } = this.getAppService()
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        const props: IGuideLineContainerProps = {
            x: slideLeft / scale,
            y: 0,
            orient: EPpGuideOrientation.ppVerticalGuide,
            guideList: this.vertGuides,
            updateGuide: this.handleMouseDown,
            isUiComponent: this.isUiComponent,
            display: !!this.guidesManager?.displayGuides,
            type: this.type,
            handleContextMenu: this.showRightMenu,
            canEdit: this.getCanEdit(),
        };
        if (!this.vertContainer) {
            this.vertContainer = new GuideLineContainer(props);
            this.addChild(this.vertContainer);
        } else {
            this.vertContainer.updateProps(props);
        }
    };

    public renderHorztContainer = () => {
        const { slideTop, scale } = this.getAppService()
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        const props = {
            x: 0,
            y: slideTop / scale,
            orient: EPpGuideOrientation.ppHorizontalGuide,
            guideList: this.horzGuides,
            updateGuide: this.handleMouseDown,
            isUiComponent: this.isUiComponent,
            display: !!this.guidesManager?.displayGuides,
            type: this.type,
            handleContextMenu: this.showRightMenu,
            canEdit: this.getCanEdit(),
        };
        if (!this.horzContainer) {
            this.horzContainer = new GuideLineContainer(props);
            this.addChild(this.horzContainer);
        } else {
            this.horzContainer.updateProps(props);
        }
    };

    public getCanEdit = () => {
        if (this.isUiComponent) return false;
        if (this.type === ESourceType.slide) return true;
        const editType = this.getAppService().editableServiceLayer?.masterLayoutEditService.editType;
        if (editType === EDIT_TYPE.SLIDE
            && (this.type === ESourceType.master || this.type === ESourceType.layout)) {
            return false;
        }

        const isMaster = this.type === ESourceType.master;
        return this.canEditMasterOrLayout(isMaster);
    };

    /**
     * 获取模板是否可编辑
     * @param isMaster
     */
    private canEditMasterOrLayout = (isMaster: boolean) => {
        const masterLayoutEditService = this.getAppService().editableServiceLayer?.masterLayoutEditService;
        const selectionService = this.getAppService().viewableServiceLayer?.selectionService;
        if (masterLayoutEditService?.editType === EDIT_TYPE.SLIDE) {
            return false;
        }
        const currentRelationModel = isMaster
            ? selectionService.currentSlide?.getRelationMasterModel()
            : selectionService.currentSlide?.getRelationLayoutModel();
        return currentRelationModel === selectionService.currentSlide;
    };

    private getAppService = () => AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;

    private getGuidesManager = () => this.getAppService().editableServiceLayer?.guidesManager;

    private getGuidesData = () => {
        const guidesManager = this.getGuidesManager();
        switch (this.type) {
            case ESourceType.slide:
                this.vertGuides = guidesManager?.slideGuidesService?.vertGuides || [];
                this.horzGuides = guidesManager?.slideGuidesService?.horzGuides || [];
                break;
            case ESourceType.master:
                this.vertGuides = guidesManager?.masterGuidesService?.vertGuides || [];
                this.horzGuides = guidesManager?.masterGuidesService?.horzGuides || [];
                break;
            case ESourceType.layout:
                this.vertGuides = guidesManager?.layoutGuidesService?.vertGuides || [];
                this.horzGuides = guidesManager?.layoutGuidesService?.horzGuides || [];
                break;
        }
        this.cachedVertGuides = this.vertGuides.map(guide => guide.pos);
        this.cachedHorzGuides = this.horzGuides.map(guide => guide.pos);
    };

    private initReaction = () => {
        switch (this.type) {
            case ESourceType.slide:
                this.initSlideReaction();
                break;
            case ESourceType.master:
                this.initMasterReaction();
                break;
            case ESourceType.layout:
                this.initLayoutReaction();
                break;
        }
        this.initChangeSlideReaction();
    };

    private initChangeSlideReaction = () => {
        // ui在切页时不需要刷新
        if (!this.isUiComponent) {
            this.reaction2 = reaction(() => [
                this.getAppService().viewableServiceLayer.selectionService.currentSlide,
            ], () => {
                requestAnimationFrame(() => {
                    if (this.getNeedRender()) {
                        this.renderGuides();
                    }
                });
            });
        }
    };

    private initSlideReaction = () => {
        this.reaction = reaction(() => [
            this.guidesManager?.displayGuides,
            this.guidesManager?.slideGuidesService.vertGuides.length,
            this.guidesManager?.slideGuidesService.horzGuides.length,
        ], () => {
            requestAnimationFrame(() => {
                this.renderGuides();
            });
        });
    };

    private initMasterReaction = () => {
        this.reaction = reaction(() => [
            this.guidesManager?.displayGuides,
            this.guidesManager?.masterGuidesService.vertGuides,
            this.guidesManager?.masterGuidesService.horzGuides,
            this.guidesManager?.masterGuidesService.vertGuides.length,
            this.guidesManager?.masterGuidesService.horzGuides.length,
        ], () => {
            requestAnimationFrame(() => {
                this.renderGuides();
            });
        });
    };

    private initLayoutReaction = () => {
        this.reaction = reaction(() => [
            this.guidesManager?.displayGuides,
            this.guidesManager?.layoutGuidesService.vertGuides,
            this.guidesManager?.layoutGuidesService.horzGuides,
            this.guidesManager?.layoutGuidesService.vertGuides.length,
            this.guidesManager?.layoutGuidesService.horzGuides.length,
        ], () => {
            requestAnimationFrame(() => {
                this.renderGuides();
            });
        });
    };

    private showRightMenu = (event: InteractionEvent, model: IGuide) => {
        if (!this.menuManager) return;
        const e = event.nativeEvent as MouseEvent;

        const disableIds = [];
        const menuData = this.menuManager.getRightMenuFromGuides(model);
        this.menuManager.hideAllMenu();
        // TODO: 依赖关系待调整。更好的做法应该是组件内抛事件给 service 单例监听，而不是渲染组件直接依赖某 service
        const { linkFloatBarService } = this.getAppService().viewableServiceLayer.linkManager;
        if (linkFloatBarService.isShown()) {
            linkFloatBarService.hide();
        }

        this.guidesManager?.setCurrentGuide(model);
        this.guidesManager?.setCurrentMouseEvent({
            e,
            pageX: e.pageX,
            pageY: e.pageY,
        });
        this.handleShowRightMenu(menuData.type, menuData.items, disableIds, {
            top: e.clientY,
            left: e.clientX + 8,
            minWidth: 200,
            subMinWidth: 200,
            onSubMenuVisibleChange: this.onSubMenuVisibleChange.bind(this, menuData),
        }, (menuType, itemID, object) => {
            menuData.callback(menuType, itemID, {
                object,
                model,
            });
        });
    };

    /**
     * 打开菜单
     * @param type
     * @param items
     * @param disabledIds
     * @param props
     * @param callback
     */
    private handleShowRightMenu = (
        type: string,
        items: Array<any>, // 整理menu时定义下各种menuItems
        disabledIds: Array<{ id: string }>,
        props: IMenuProps,
        callback,
    ) => {
        this.getAppService().viewableServiceLayer.rightMenuManager.promise().then((rightMenuManager) => {
            rightMenuManager.showMenu(type, items, disabledIds, props, (...args) => {
                rightMenuManager.hideMenu(type);
                callback(...args);
            });
        });
    };

    /**
     * 子菜单状态维护
     * @param menuData
     * @param visible
     * @param id
     */
    private onSubMenuVisibleChange = (
        menuData: any, // 整理menu时定义下各种menuItems
        visible: boolean,
        id: string,
    ) => {
        menuData.callback(MenuType.POP_MENU, id, {
            TriggerFrom: INSERT_SLIDE_ORIGIN_TYPE.LEFT,
            visible,
        });
    };

    /**
     * 开始拖拽参考线
     * @param e
     */
    private handleMouseDown = (event: InteractionEvent, guide: IGuide, currentComponent: IGuideLineComponent) => {
        const e = event.nativeEvent as MouseEvent;
        if (e.button !== 0 || !this.guidesManager) return;
        this.guidesManager.setCurrentGuide(guide);
        this.cacheMousePosition.x = e.clientX;
        this.cacheMousePosition.y = e.clientY;
        this.cacheMousePosition.originPos = guide.pos;
        this.currentComponent = currentComponent;

        document.addEventListener('mousemove', this.handleMouseMove);
        document.addEventListener('mouseup', this.handleMouseUp);
    };

    /**
     * 处理拖拽中
     * @param e
     */
    private handleMouseMove = (e: MouseEvent) => {
        if (!this.guidesManager) return;
        const changedPos = this.getChangedPos(e);
        const { guide } = this.guidesManager.currentGuideModelMap;
        transaction(() => {
            if (guide) {
                guide.pos = changedPos;
                let direction; let message;
                if (guide.orient === EPpGuideOrientation.ppVerticalGuide) {
                    const res = this.handleVertPos(guide);
                    direction = res.direction;
                    message = res.message;
                } else {
                    const res = this.handleHoriPos(guide);
                    direction = res.direction;
                    message = res.message;
                }
                message = String(convertPxToCm(Math.abs(message)).toFixed(2));

                ArrowTips.showTips({
                    left: e.clientX,
                    top: e.clientY,
                    direction,
                    message,
                });
            }
        });
    };

    /**
     * 计算变动的坐标
     * @param e
     */
    private getChangedPos = (e: MouseEvent): number => {
        if (!this.guidesManager) return 0;
        const { scale } = this.getAppService()
            .viewableServiceLayer.editAreaCanvasSizeManager.getCanvasSize();
        const x = e.clientX - this.cacheMousePosition.x;
        const y = e.clientY - this.cacheMousePosition.y;
        const { guide } = this.guidesManager.currentGuideModelMap;
        let changedPos = 0;
        if (guide) {
            const { orient } = guide;
            if (orient === EPpGuideOrientation.ppVerticalGuide) {
                changedPos = this.cacheMousePosition.originPos + (x / scale);
                if (changedPos > this.slideWidth) changedPos = this.slideWidth;
            } else {
                changedPos = this.cacheMousePosition.originPos + (y / scale);
                if (changedPos > this.slideHeight) changedPos = this.slideHeight;
            }

            // 参考线不能超过画布上下限
            if (changedPos < 0) changedPos = 0;
            let uiComponentContainer;
            const operationService = this.guidesManager.getOperationService();
            if (guide.orient === EPpGuideOrientation.ppVerticalGuide) {
                uiComponentContainer = operationService?.vertUiComponentContainer;
            } else {
                uiComponentContainer = operationService?.horzUiComponentContainer;
            }
            uiComponentContainer.componentMap.get(guide)?.renderGuideLine(changedPos, guide.color);
        }
        return changedPos;
    };


    /**
     * 处理拖拽结束
     * @param e
     */
    private handleMouseUp = (e: MouseEvent) => {
        if (!this.guidesManager) return;
        const { guide } = this.guidesManager.currentGuideModelMap;
        this.guidesManager.setCurrentGuidePos(guide?.pos || 0);
        this.currentComponent?.renderGuideLine(guide?.pos || 0, guide?.color || '');
        if (guide) {
            let slideUiComponentContainer;
            if (guide.orient === EPpGuideOrientation.ppVerticalGuide) {
                slideUiComponentContainer = this.guidesManager.getOperationService()?.vertUiComponentContainer;
            } else {
                slideUiComponentContainer = this.guidesManager.getOperationService()?.horzUiComponentContainer;
            }
            slideUiComponentContainer?.componentMap.get(guide)?.renderGuideLine(guide?.pos || 0, guide.color);
        }
        ArrowTips.hideTips();
        document.removeEventListener('mousemove', this.handleMouseMove);
        document.removeEventListener('mouseup', this.handleMouseUp);
    };

    /**
     * 处理垂直参考线吸附
     * @param guide
     * @private
     */
    private handleVertPos(guide: IGuide) {
        if (Math.abs(guide.pos - (this.slideWidth / 2)) < STICKY_WIDTH) {
            guide.pos = this.slideWidth / 2;
        }

        if (this.gridLineManager?.isGridAlign) {
            guide.pos = this.computeAbsorbGridLine(
                guide.pos,
                this.slideWidth,
                this.gridLineManager?.absorbPointX || [],
            );
        }

        const direction = guide.pos > this.slideWidth / 2 ? EArrowDirection.right : EArrowDirection.left;
        const message = guide.pos - (this.slideWidth / 2);
        return { direction, message };
    }

    /**
     * 处理水平参考线吸附
     * @param guide
     * @private
     */
    private handleHoriPos = (guide: IGuide) => {
        if (Math.abs(guide.pos - (this.slideHeight / 2)) < STICKY_WIDTH) {
            guide.pos = this.slideHeight / 2;
        }

        if (this.gridLineManager?.isGridAlign) {
            guide.pos = this.computeAbsorbGridLine(
                guide.pos,
                this.slideHeight,
                this.gridLineManager?.absorbPointY || [],
            );
        }

        const direction = guide.pos > this.slideHeight / 2 ? EArrowDirection.down : EArrowDirection.up;
        const message = guide.pos - (this.slideHeight / 2);
        return { direction, message };
    };

    /**
     * 计算网格吸附
     * @param pos
     * @param maxLimitPoint
     * @param absorbPoints
     */
    private computeAbsorbGridLine = (pos: number, maxLimitPoint: number, absorbPoints: Array<number>): number => {
        const points = (absorbPoints).filter(point => point > 0 && point < maxLimitPoint);
        points.push(0);
        points.push(maxLimitPoint);

        let absorbPoint = Number.MAX_SAFE_INTEGER;
        let res = pos;
        points.forEach((point) => {
            const diffAbs = Math.abs(pos - point);
            if (diffAbs < absorbPoint) {
                absorbPoint = diffAbs;
                res = point;
            }
        });

        return res;
    };

    /**
     * 获取切页时，组件内部是否要刷新，做个diff
     */
    // eslint-disable-next-line complexity
    private getNeedRender = (): boolean => {
        const guidesManager = this.getGuidesManager();
        let vertGuides: Array<IGuide> = [];
        let horzGuides: Array<IGuide> = [];
        switch (this.type) {
            case ESourceType.slide:
                vertGuides = guidesManager?.slideGuidesService?.vertGuides || [];
                horzGuides = guidesManager?.slideGuidesService?.horzGuides || [];
                break;
            case ESourceType.master:
                vertGuides = guidesManager?.masterGuidesService?.vertGuides || [];
                horzGuides = guidesManager?.masterGuidesService?.horzGuides || [];
                break;
            case ESourceType.layout:
                vertGuides = guidesManager?.layoutGuidesService?.vertGuides || [];
                horzGuides = guidesManager?.layoutGuidesService?.horzGuides || [];
                break;
        }
        const needRenderVert = this.compareGuidesDiff(this.cachedVertGuides, vertGuides);
        const needRenderHorz = this.compareGuidesDiff(this.cachedHorzGuides, horzGuides);
        return needRenderVert || needRenderHorz;
    };

    private compareGuidesDiff = (cachedGuides: Array<number>, newGuides: Array<IGuide>) => {
        let needRender = false;
        if (cachedGuides?.length !== newGuides.length) {
            needRender = true;
        }

        cachedGuides?.forEach((pos, index) => {
            if (pos !== newGuides?.[index]?.pos) {
                needRender = true;
            }
        });

        return needRender;
    };
}
